// @ts-strict-ignore
import { useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { css, styled } from '@mui/material/styles';
import { trackMultiSelectionInputUsageAnalyticsEvent } from 'analytics/events/input-usage';

import { find, findIndex, isArray } from 'lodash/fp';

import { observer } from 'mobx-react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { ActionMeta } from 'react-select/dist/declarations/src/types';

import { useStores } from 'mobx/hooks/useStores';

import { TicketSubTypeOption } from 'utils/TicketType.utils';
import { defaultSymptomUrgencyValue } from 'utils/urgencyMappings';

import Patient from 'models/Patient';
import { TicketTypeKind } from 'models/TicketTypes';

import { isSymptomTicket } from 'components/Ticket/TicketForms/OperatorEditTicketInfoForm';
import { TicketFormField } from 'components/Ticket/TicketForms/ticket.shared';
import { FormTicketTypeMultiAutocomplete } from 'components/UIkit/atoms/Dropdown';
import { SelectActionMetaName } from 'components/UIkit/atoms/Dropdown/SelectUtils';
import { Text } from 'components/UIkit/atoms/Text';

import { SelectedOperatorCreateTicketInfoFormItem } from './SelectedOperatorCreateTicketInfoFormItem';
import {
  CustomTicketTypeOption,
  disableTicketTypeSelectFilter,
  useTicketTypeSearch,
  useTicketTypesOptions
} from './TicketsInfoFormFragments/OperatorTicketsInfoFormCommon';
import { useTicketTypeDefaultFields } from './TicketTypeDefaultFieldsProvider';

const useOperatorTicketsInfoForm = () => {
  const { getTicketTypeDefaultFields } = useTicketTypeDefaultFields();
  const { control, formState } = useFormContext();
  const tickets: TicketFormField[] = useWatch({ name: 'tickets', control });

  const [searchValue, setSearchValue] = useState('');

  const {
    fields: selectedTicketFields,
    remove,
    append,
    update
  } = useFieldArray({
    control,
    name: 'tickets',
    shouldUnregister: true
  });

  const { errors } = formState;
  const { ticketTypesStore } = useStores();

  const groupedTicketTypesOptions = useTicketTypesOptions({
    // filter deleted or not allowed types
    filterFn: (node) => node.isActive && node.reportableInAll && !node.isFever,
    showFullNameForNonSymptomTicketTypes: true
  });

  // Get all selected ticket subtype IDs
  const selectedTicketSubTypeIds = useMemo(
    () => tickets.flatMap((ticket: TicketFormField) => ticket.ticketSubTypeIds || []),
    [tickets]
  );

  const filteredOptions = useTicketTypeSearch(
    groupedTicketTypesOptions,
    searchValue,
    selectedTicketSubTypeIds
  );

  const handleSymptomTicketTypeRemove = (removedTicketType: TicketSubTypeOption) => {
    const existingTicketFormField = find<TicketFormField>(
      (item) => item.parentId === removedTicketType.parentId,
      tickets
    );

    const isMultiSymptomsTicket =
      existingTicketFormField &&
      (existingTicketFormField?.ticketTypeSelectorValue as TicketSubTypeOption[]).length > 1;

    if (isMultiSymptomsTicket) {
      const existingSymptomFieldIndex = findIndex(existingTicketFormField, tickets);

      update(existingSymptomFieldIndex, {
        ...existingTicketFormField,
        ticketSubTypeIds: existingTicketFormField.ticketSubTypeIds.filter(
          (id) => id !== removedTicketType.value
        ),
        ticketTypeSelectorValue: (
          existingTicketFormField.ticketTypeSelectorValue as TicketSubTypeOption[]
        ).filter((selector) => selector.value !== removedTicketType.value)
      });

      return;
    }

    const indexToRemove = tickets.findIndex(
      (ticket: TicketFormField) =>
        isArray(ticket.ticketTypeSelectorValue) || isSymptomTicket(ticket, ticketTypesStore) //if ticketTypeSelectorValue is an array -> this is the symptoms ticket
    );

    remove(indexToRemove);
  };

  const handleTicketTypeRemove = (
    selectedSubTypesOptions: TicketSubTypeOption[],
    actionMeta: ActionMeta<TicketSubTypeOption>
  ) => {
    if (!selectedSubTypesOptions) {
      const contextDefaults = getTicketTypeDefaultFields();
      update(0, {
        ...selectedTicketFields[0],
        urgency: contextDefaults.urgency,
        notes: '',
        assignee: contextDefaults.assignee,
        locationId: contextDefaults.locationId,
        providerId: contextDefaults.providerId,
        categoryId: '',
        ticketSubTypeIds: [],
        parentId: null,
        ticketTypeSelectorValue: null
      });
      return;
    }

    const removedTicketType = actionMeta.removedValue;

    const isSymptomTicketType = removedTicketType.parentKind === TicketTypeKind.symptom;

    if (isSymptomTicketType) {
      handleSymptomTicketTypeRemove(removedTicketType);
    } else {
      //in case we don't have a ticket type, we want to render the default empty one
      const indexToRemove = tickets.findIndex(
        (ticket: TicketFormField) => ticket.ticketSubTypeIds[0] === removedTicketType.value
      );
      remove(indexToRemove);
    }
  };

  const handleTicketTypeSelect = (
    selectedSubTypesOptions: TicketSubTypeOption[],
    actionMeta: ActionMeta<TicketSubTypeOption>
  ) => {
    const newTicketType = actionMeta.option;
    const categoryNode = ticketTypesStore.getCategoryByParentId(newTicketType.parentId);
    const parentNode = ticketTypesStore.getTicketTypeByKind(
      newTicketType.parentId,
      newTicketType.parentKind as TicketTypeKind
    );

    const isSymptomTicketType = newTicketType.parentKind === TicketTypeKind.symptom;
    const isFirstTicket = selectedSubTypesOptions.length === 1;

    const context = getTicketTypeDefaultFields();
    const urgency = isSymptomTicketType ? defaultSymptomUrgencyValue : context.urgency;

    const newDefaultTicketType: Partial<TicketFormField> = {
      urgency,
      suggestedUrgency: urgency.value,
      notes: '',
      categoryId: String(categoryNode.id),
      ticketSubTypeIds: [newTicketType.value],
      parentId: newTicketType.parentId,
      ticketTypeSelectorValue: isSymptomTicketType ? [newTicketType] : newTicketType,
      assignee: context.assignee,
      locationId: context.locationId,
      providerId: context.providerId
    };

    const newFirstDefaultTicketType: Partial<TicketFormField> = {
      ...selectedTicketFields[0],
      urgency,
      suggestedUrgency: urgency.value,
      assignee: context.assignee,
      locationId: context.locationId,
      providerId: context.providerId,
      notes: tickets[0].notes,
      categoryId: String(categoryNode.id),
      ticketSubTypeIds: [newTicketType.value],
      parentId: newTicketType.parentId,
      ticketTypeSelectorValue: isSymptomTicketType ? [newTicketType] : newTicketType
    };

    if (isFirstTicket) {
      update(0, newFirstDefaultTicketType);
      return;
    }

    // For symptom management we need to check for existing form fields
    const existingTicketFormField = find<TicketFormField>(
      (item) => item.parentId === newTicketType.parentId,
      tickets
    );

    // If it's symptom management we need to update form field not add a new one
    if (existingTicketFormField && parentNode.kind === TicketTypeKind.symptom) {
      const existingSymptomFieldIndex = findIndex(existingTicketFormField, tickets);

      const newTicketTypeSelectorValue = isArray(existingTicketFormField.ticketTypeSelectorValue)
        ? [
            ...(existingTicketFormField.ticketTypeSelectorValue as TicketSubTypeOption[]),
            newTicketType
          ]
        : [existingTicketFormField.ticketTypeSelectorValue, newTicketType];

      update(existingSymptomFieldIndex, {
        ...existingTicketFormField,
        ticketSubTypeIds: [...existingTicketFormField.ticketSubTypeIds, newTicketType.value],
        ticketTypeSelectorValue: newTicketTypeSelectorValue
      });
    } else {
      // either first of type symptom management or just a non-symptom operator type
      // Adding default values to new type
      append(newDefaultTicketType);
    }
  };

  const onTicketTypesChange = (
    selectedSubTypesOptions: TicketSubTypeOption[],
    actionMeta: ActionMeta<TicketSubTypeOption>,
    eventKey: string | null
  ) => {
    const { action } = actionMeta;
    trackMultiSelectionInputUsageAnalyticsEvent(
      actionMeta,
      'Ticket Types Main Dropdown',
      eventKey === 'Enter'
    );

    if (action === SelectActionMetaName.Select) {
      handleTicketTypeSelect(selectedSubTypesOptions, actionMeta);
    }

    if (action === SelectActionMetaName.Remove) {
      handleTicketTypeRemove(selectedSubTypesOptions, actionMeta);
    }
  };

  const handleSearchInputChange = (newValue: string) => {
    setSearchValue(newValue);
  };

  return {
    filteredOptions,
    onTicketTypesChange,
    searchValue,
    handleSearchInputChange,
    selectedTicketFields,
    tickets,
    remove,
    update,
    errors
  };
};

export const OperatorTicketsInfoForm = observer(({ patient }: { patient: Patient }) => {
  const {
    filteredOptions,
    onTicketTypesChange,
    searchValue,
    handleSearchInputChange,
    selectedTicketFields,
    tickets,
    remove,
    update,
    errors
  } = useOperatorTicketsInfoForm();

  return (
    <Box display="flex" flexDirection="column" gap={20}>
      <StyledCreateTicketGridItem p={20}>
        <Box flex={1}>
          <Box pb={20}>
            <Text variant="h4" component="h4">
              Which tickets would you like to create?
            </Text>
            <Text variant="subtext" color="secondary" component="div">
              You can select more than one ticket type to create multiple tickets during a single
              call.
            </Text>
          </Box>
          <FormTicketTypeMultiAutocomplete
            warnOnRemove
            name="ticketTypes"
            placeholder="Select Ticket Types"
            aria-label="Add Ticket Types"
            options={filteredOptions}
            onChange={onTicketTypesChange}
            backspaceRemovesValue={false}
            isClearable={false}
            filterOption={disableTicketTypeSelectFilter}
            testHook="ticketTypes"
            closeMenuOnSelect
            hideSelectedOptions={false}
            inputValue={searchValue}
            onInputChange={handleSearchInputChange}
            components={{
              Option: CustomTicketTypeOption // render option
            }}
          />
        </Box>
      </StyledCreateTicketGridItem>

      <StyledTicketsContainer>
        {selectedTicketFields.map((selectedTicketType, index) => (
          <SelectedOperatorCreateTicketInfoFormItem
            key={selectedTicketType.id}
            selectedTicketType={selectedTicketType as unknown as TicketFormField & { id: string }}
            index={index}
            tickets={tickets}
            remove={remove}
            update={update}
            patient={patient}
            errors={errors}
          />
        ))}
      </StyledTicketsContainer>
    </Box>
  );
});

const StyledCreateTicketGridItem = styled(Box)(
  ({ theme }) =>
    css`
      display: flex;
      border: 1px solid ${theme.palette.natural.border};
      border-radius: ${theme.borderRadius.medium};
      transition: border-color 0.2s ease-in-out;
      background-color: ${theme.palette.natural.white};
      color: ${theme.palette.secondary.dark};
    `
);

const StyledTicketsContainer = styled(Box)(
  ({ theme }) =>
    css`
      .styled-fields-wrapper {
        margin-bottom: ${theme.spacing(40)};
      }
    `
);
